import React, { useState } from 'react';
import styled from 'styled-components';

import ArrowDown from '../../../svgs/ArrowDown';
import BackIcon from '../../../svgs/BackIcon';

import type { MediaFormat } from '../../common/FormatDropdown';

type Props = {
  onDownload: (
    e: React.MouseEvent<HTMLButtonElement>,
    format: MediaFormat,
  ) => void;
  onBack: () => void;
};

const formatOptions: MediaFormat[] = ['png', 'mp4', 'gif'];

const QuoteDownloadDropdown = ({ onDownload, onBack }: Props) => {
  const [selectedFormat, setSelectedFormat] = useState<MediaFormat>('png');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const handleFormatSelect = (format: MediaFormat) => {
    setSelectedFormat(format);
    setIsDropdownOpen(false);
  };

  const handleDownloadClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onDownload(e, selectedFormat);
  };

  return (
    <MenuContainer data-testid="quote-download-dropdown">
      <Header>
        <BackButton onClick={onBack}>
          <BackIcon
            width="17"
            height="13"
            strokeColor="var(--text-secondary)"
          />
        </BackButton>
        <Title>Download Options</Title>
      </Header>

      <FormatSection>
        <DropdownWrapper>
          <DropdownButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <FormatText>{selectedFormat.toUpperCase()}</FormatText>
            <ChevronWrapper>
              <ArrowDown
                width="8"
                height="5"
                strokeColor="var(--text-secondary)"
                strokeWidth="1.5"
              />
            </ChevronWrapper>
          </DropdownButton>

          {isDropdownOpen && (
            <DropdownMenu>
              {formatOptions.map(format => (
                <DropdownOption
                  key={format}
                  data-testid={`quote-save-${format}`}
                  onClick={() => handleFormatSelect(format)}
                >
                  {format.toUpperCase()}
                </DropdownOption>
              ))}
            </DropdownMenu>
          )}
        </DropdownWrapper>
      </FormatSection>

      <DownloadButton onClick={handleDownloadClick}>Download</DownloadButton>
    </MenuContainer>
  );
};

export default QuoteDownloadDropdown;

const MenuContainer = styled.div`
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--elevation-toast);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 219px;
`;

const Header = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
`;

const BackButton = styled.button`
  width: 17px;
  height: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
`;

const Title = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: normal;
  color: var(--text-secondary);
  margin: 0;
  white-space: pre-wrap;
`;

const FormatSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 31px;
  align-items: flex-start;
  width: 100%;
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const DropdownButton = styled.button`
  background: var(--background);
  border: 0.762px solid var(--border);
  border-radius: 6.095px;
  box-shadow: var(--elevation-xs);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7.619px 10.667px;
  overflow: hidden;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: var(--surface-elevated);
  }
`;

const FormatText = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 15.238px;
  color: var(--text-secondary);
  margin: 0;
  padding: 0 1.524px;
`;

const ChevronWrapper = styled.div`
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 6px;
  box-shadow: var(--elevation-toast);
  z-index: 1001;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--spacing-xs);
`;

const DropdownOption = styled.button`
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: var(--text-secondary);
  border-radius: var(--radius-button);

  &:hover {
    background: var(--surface-elevated);
  }
`;

const DownloadButton = styled.button`
  background: var(--primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  height: 32px;
  overflow: hidden;
  cursor: pointer;
  border: none;
  width: 100%;

  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: normal;
  color: var(--primary-foreground);
  text-align: center;

  &:hover {
    background: var(--primary-hover);
  }

  &:active {
    background: var(--primary-pressed);
  }
`;
